import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "./client";
import { endpoints } from "./endpoints";
import { queryKeys } from "./keys";
import type { ApiEnvelope } from "./types";
import type {
  OnboardingSubmitResult,
  UpdateVendorProfileRequest,
  VendorOnboarding,
  VendorOnboardingStatus,
  VendorProfile,
} from "./types/marketplace";

const inlineErrorMeta = { silentError: true } as const;

async function getOverview(): Promise<VendorOnboarding> {
  const { data } = await apiClient.get<ApiEnvelope<VendorOnboarding>>(
    endpoints.onboarding.overview,
  );
  return data.data;
}

async function getProfile(): Promise<VendorProfile> {
  const { data } = await apiClient.get<ApiEnvelope<VendorProfile>>(endpoints.onboarding.profile);
  return data.data;
}

async function updateProfile(payload: UpdateVendorProfileRequest): Promise<VendorProfile> {
  const { data } = await apiClient.patch<ApiEnvelope<VendorProfile>>(
    endpoints.onboarding.profile,
    payload,
  );
  return data.data;
}

/** Fetches vendor org onboarding status. Used by login (status-first) and the zone gate. */
export async function fetchOnboardingStatus(): Promise<VendorOnboardingStatus> {
  const { data } = await apiClient.get<ApiEnvelope<VendorOnboardingStatus>>(
    endpoints.onboarding.status,
  );
  return data.data;
}

async function submitOnboarding(): Promise<OnboardingSubmitResult> {
  // The client always sends Content-Type: application/json. Omit the body and
  // the request has no payload; the API ignores any body, so send {}.
  const { data } = await apiClient.post<ApiEnvelope<OnboardingSubmitResult>>(
    endpoints.onboarding.submit,
    {},
  );
  return data.data;
}

/** Overview: canSubmit, submission, freeze source of truth. */
export function useVendorOnboarding() {
  return useQuery({
    queryKey: queryKeys.onboarding.overview,
    queryFn: getOverview,
    meta: inlineErrorMeta,
    retry: false,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchInterval: (query) => (query.state.data?.status === "ACTIVE" ? false : 30_000),
  });
}

export function useVendorProfile() {
  return useQuery({
    queryKey: queryKeys.onboarding.profile,
    queryFn: getProfile,
    meta: inlineErrorMeta,
    retry: false,
  });
}

/** Poll every 30 s while the org is not yet ACTIVE so the vendor sees activation without a refresh. */
export function useOnboardingStatus() {
  return useQuery({
    queryKey: queryKeys.onboarding.status,
    queryFn: fetchOnboardingStatus,
    meta: inlineErrorMeta,
    retry: false,
    // Always re-validate on mount so a previous session's ACTIVE cache cannot unlock the portal.
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchInterval: (query) => (query.state.data?.status === "ACTIVE" ? false : 30_000),
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateProfile,
    meta: inlineErrorMeta,
    onSuccess: (profile) => {
      qc.setQueryData(queryKeys.onboarding.profile, profile);
      qc.invalidateQueries({ queryKey: queryKeys.onboarding.status });
      qc.invalidateQueries({ queryKey: queryKeys.onboarding.overview });
    },
  });
}

export function useSubmitOnboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: submitOnboarding,
    meta: inlineErrorMeta,
    onSuccess: (result) => {
      qc.setQueryData(queryKeys.onboarding.overview, result.onboarding);
      qc.setQueryData(queryKeys.onboarding.status, result.onboardingStatus);
      qc.invalidateQueries({ queryKey: queryKeys.onboarding.all });
      qc.invalidateQueries({ queryKey: queryKeys.approvals.all });
      qc.invalidateQueries({ queryKey: queryKeys.services.all });
    },
  });
}
